import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

const EMPTY = { author: "", location: "", rating: 5, text: "", date: "", source: "Airbnb", gite_slug: "chalet-6", published: false };

const ReviewRow = ({ r, onToggle, onDelete }) => (
  <div className={`flex items-start justify-between gap-4 border bg-panel p-5 ${r.published ? "border-white/10" : "border-brass/40"}`}
    data-testid={`admin-review-${r.id}`}>
    <div>
      <p className="text-sm font-semibold text-bone">
        {r.author} <span className="font-normal text-muted-foreground">· {r.source || "Site"} · {r.rating}★</span>
        {!r.published && <span className="ml-2 bg-brass/20 px-2 py-0.5 text-[10px] uppercase tracking-wider text-brass">à valider</span>}
      </p>
      <p className="mt-1.5 text-sm text-muted-foreground">{r.text}</p>
    </div>
    <div className="flex shrink-0 items-center gap-3">
      <button className="text-xs underline" data-testid={`toggle-review-${r.id}`} onClick={() => onToggle(r)}>
        {r.published ? "Masquer" : "Publier"}
      </button>
      <button className="text-destructive" data-testid={`delete-review-${r.id}`} onClick={() => onDelete(r)}>
        <Trash2 size={15} />
      </button>
    </div>
  </div>
);

export default function ReviewsTab() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const load = useCallback(() => api.get("/admin/reviews").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/reviews", { ...form, rating: Number(form.rating), published: false });
      toast.success("Avis enregistré — un email de validation vient de vous être envoyé");
      setForm(EMPTY);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const toggle = async (r) => { await api.patch(`/admin/reviews/${r.id}`, { published: !r.published }); load(); };
  const remove = async (r) => { await api.delete(`/admin/reviews/${r.id}`); toast.success("Supprimé"); load(); };

  return (
    <div className="grid gap-8 lg:grid-cols-12" data-testid="reviews-tab">
      <form onSubmit={add} className="space-y-4 border border-white/10 bg-panel p-6 lg:col-span-5" data-testid="review-form">
        <h3 className="font-display text-xl text-bone">Ajouter un avis reçu (Airbnb / Google)</h3>
        <input required placeholder="Prénom de l'auteur" className="field !py-2" data-testid="review-author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} />
        <select className="field !py-2" data-testid="review-gite" value={form.gite_slug} onChange={(e) => setForm({ ...form, gite_slug: e.target.value })}>
          <option value="chalet-6">Le Chalet</option>
          <option value="gite-4">La Ferme</option>
          <option value="ensemble-10">Le Chalet + La Ferme</option>
        </select>
        <select className="field !py-2" data-testid="review-source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
          {["Airbnb", "Google", "Booking", "Site"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input placeholder="Période (ex : Mars 2026)" className="field !py-2" data-testid="review-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        <select className="field !py-2" data-testid="review-rating" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })}>
          {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} étoile{n > 1 ? "s" : ""}</option>)}
        </select>
        <textarea required rows={4} placeholder="Texte de l'avis" className="field" data-testid="review-text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} />
        <button className="btn-primary w-full !py-2.5" data-testid="review-submit"><Plus size={15} /> Enregistrer</button>
        <p className="text-xs leading-relaxed text-muted-foreground">
          L'avis reste masqué : vous recevez un email avec le contenu et deux boutons pour l'afficher ou le refuser.
        </p>
      </form>

      <div className="space-y-3 lg:col-span-7">
        {rows.map((r) => <ReviewRow key={r.id} r={r} onToggle={toggle} onDelete={remove} />)}
        {!rows.length && <p className="border border-white/10 bg-panel p-8 text-center text-sm text-muted-foreground">
          Aucun avis. Les clients en reçoivent la demande par email le jour de leur départ ; vous validez ici avant publication.
        </p>}
      </div>
    </div>
  );
}
